"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Settings, ChevronRight, Loader2, ArrowLeft, Home } from "lucide-react";
import { Button } from "../../../components/ui/button"
import { useToast } from "../../../components/ui/use-toast"
import SKUConfigModal from "../../Inventory/SKUs/SKUConfigModal"
import CustomColumnsModal from "../../Inventory/CustomColumns/CustomColumnsModal"
import {ProductConfigModal} from "../../Products/Components/ProductConfigModal"
import ApprovalRulesModal from "./ApprovalRulesModal"
import { Badge } from "../../../components/ui/badge"
import BudgetConfigurationModal from "./BudgetConfigurationModal"
import CashFlowConfigurationModal from "./CashFlowConfigurationModal"
import IPRRequirementsModal from "./IPRRequirementsModal"
import CategoriesCrud from "../../Inventory/categories/CategoriesCrud"
import BusinessIntegrations from "../BusinessIntegrations"
import UsersSection from "../../User/UsersSection"
import ClientsSection from "../../Clients/Section/ClientsSection"
import SuppliersManagement from "../../Suppliers/SuppliersManagment"

export default function ConfigurationSection({
  clientId,
  onBack,
  mantisBusiness,
}) {
  const [loading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const { toast } = useToast();

  // Configuration sections data
  const sections = [
    {
      id: "business_rules",
      name: "Reglas de Negocio",
      icon: "⚖️",
      gradient: "from-blue-500 to-indigo-600",
      description:
        "Configure las reglas y lógica de aprobación de su organización",
      options: [
        {
          id: "approval_rules",
          name: "Reglas de Aprobación",
          description:
            "Configure la lógica de aprobación del flujo de trabajo y las condiciones",
          component: ApprovalRulesModal,
          icon: "🔒",
          status: "active",
        },
        {
          id: "epurchase_requirements",
          name: "Requisitos de Compra",
          description:
            "Gestione los requisitos obligatorios y opcionales para las solicitudes de compra",
          component: IPRRequirementsModal,
          icon: "📋",
          status: "active",
        },
      ],
    },
    {
      id: "categories",
      name: "Configuraciones de Categorías",
      icon: "🏷️",
      gradient: "from-violet-500 to-purple-600",
      description: "Gestione categorías, subcategorías y centros de costos",
      options: [
        {
          id: "categories_subcategories",
          name: "Categorías y Subcategorías",
          description:
            "Gestione las categorías y subcategorías del sistema de inventario",
          component: CategoriesCrud,
          icon: "📁",
          status: "active",
        },
        {
          id: "cost_centers",
          name: "Centros de Costos",
          description:
            "Gestione los centros de costos asociados a las categorías",
          component: CategoriesCrud,
          icon: "🏢",
          status: "active",
          defaultTab: "costcenters",
        },
      ],
    },
    {
      id: "budget",
      name: "Presupuesto",
      icon: "💰",
      gradient: "from-green-500 to-emerald-600",
      description:
        "Gestione la configuración de presupuestos y carga masiva de datos",
      options: [
        {
          id: "budget_upload",
          name: "Configuración de Presupuesto",
          description:
            "Cargue y configure datos de presupuesto mediante archivos Excel",
          component: BudgetConfigurationModal,
          icon: "📊",
          status: "active",
        },
      ],
    },
    {
      id: "cash_flow",
      name: "Flujo de Caja",
      icon: "💧",
      gradient: "from-cyan-500 to-blue-600",
      description: "Gestione y analice el flujo de efectivo de su organización",
      options: [
        {
          id: "cash_flow_upload",
          name: "Configuración de Flujo de Caja",
          description:
            "Cargue y configure datos de flujo de efectivo mediante archivos Excel",
          component: CashFlowConfigurationModal,
          icon: "💳",
          status: "active",
        },
      ],
    },
    {
      id: "inventory",
      name: "Inventario",
      icon: "📦",
      gradient: "from-emerald-500 to-teal-600",
      description: "Gestione la configuración de inventario y productos",
      options: [
        {
          id: "sku_config",
          name: "Configuración de SKU",
          description: "Configure el formato SKU y las reglas de validación",
          component: SKUConfigModal,
          icon: "🏷️",
          status: "active",
        },
        {
          id: "custom_columns",
          name: "Columnas Personalizadas",
          description:
            "Administre columnas personalizadas para elementos de inventario",
          component: CustomColumnsModal,
          icon: "📝",
          status: "active",
        },
      ],
    },
    {
      id: "products",
      name: "Productos",
      icon: "🛍️",
      gradient: "from-purple-500 to-pink-600",
      description: "Configure productos y formularios de creación",
      options: [
        {
          id: "form_configuration",
          name: "Configuración de Formularios",
          description:
            "Personalice los campos del formulario de creación y edición de productos",
          component: ProductConfigModal,
          icon: "📋",
          status: "active",
        },
      ],
    },
    {
      id: "integrations",
      name: "Integraciones",
      icon: "🔗",
      gradient: "from-orange-500 to-red-600",
      description: "Configure integraciones con sistemas externos",
      options: [
        {
          id: "data_sources",
          name: "Fuentes de Datos",
          description:
            "Conecte y gestione sus fuentes de información externas como Gmail, Outlook y otros sistemas",
          component: BusinessIntegrations,
          icon: "🔌",
          status: "active",
        },
      ],
    },
    {
      id: "users",
      name: "Usuarios",
      icon: "👥",
      gradient: "from-indigo-500 to-purple-600",
      description: "Gestione usuarios del sistema y grupos de trabajo",
      options: [
        {
          id: "system_users",
          name: "Usuarios del Sistema",
          description: "Administre usuarios del sistema, permisos y accesos",
          component: UsersSection,
          icon: "👤",
          status: "active",
        },
        {
          id: "system_groups",
          name: "Grupos del Sistema",
          description: "Gestione grupos de usuarios y roles de trabajo",
          component: UsersSection,
          icon: "👥",
          status: "active",
          defaultTab: "SYSTEM_GROUPS",
        },
      ],
    },
    {
      id: "clients",
      name: "Clientes",
      icon: "🏢",
      gradient: "from-orange-500 to-amber-600",
      description: "Gestione su base de clientes y contactos comerciales",
      options: [
        {
          id: "clients_management",
          name: "Gestión de Clientes",
          description: "Administre clientes, contactos y información comercial",
          component: ClientsSection,
          icon: "👥",
          status: "active",
        },
      ],
    },
    {
      id: "suppliers",
      name: "Proveedores",
      icon: "🚚",
      gradient: "from-green-500 to-emerald-600",
      description: "Gestione proveedores y relaciones comerciales",
      options: [
        {
          id: "suppliers_management",
          name: "Gestión de Proveedores",
          description: "Administre proveedores, calificaciones y contactos",
          component: SuppliersManagement,
          icon: "🏭",
          status: "active",
        },
      ],
    },
    {
      id: "security",
      name: "Seguridad",
      icon: "🔐",
      gradient: "from-red-500 to-rose-600",
      description: "Configure políticas de seguridad y acceso",
      options: [
        {
          id: "permissions",
          name: "Permisos",
          description: "Configure permisos y roles de usuario",
          icon: "👥",
          status: "coming_soon",
        },
        {
          id: "audit_logs",
          name: "Registros de Auditoría",
          description: "Configure registro de auditoría y monitoreo",
          icon: "📋",
          status: "coming_soon",
        },
      ],
    },
  ];

  // Mapping between enabledSections strings and section IDs
  const sectionMapping = {
    BUSINESS_RULES: "business_rules",
    CATEGORIES: "categories",
    BUDGETS: "budget",
    USERS: "users",
    CASH_FLOW: "cash_flow",
    INVENTORY: "inventory",
    PRODUCTS: "products",
    INTEGRATIONS: "integrations",
    CLIENTS: "clients",
    SUPPLIERS: "suppliers",
    SECURITY: "security",
  };

  // Filter sections based on enabledSections from mantisBusiness
  const getFilteredSections = () => {
    // If businessConfigurations is null or enabledSections is not available, return all sections
    if (!mantisBusiness?.businessConfigurations?.enabledSections) {
      return sections;
    }

    const enabledSections =
      mantisBusiness.businessConfigurations.enabledSections;
    return sections.filter((section) => {
      const sectionKey = Object.keys(sectionMapping).find(
        (key) => sectionMapping[key] === section.id
      );
      return enabledSections.includes(sectionKey);
    });
  };

  const filteredSections = getFilteredSections();

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setSelectedOption(null);
  };

  const handleOptionSelect = (option) => {
    if (option.status === "coming_soon") {
      toast({
        title: "Próximamente",
        description: "Esta funcionalidad estará disponible pronto",
        variant: "default",
      });
      return;
    }
    setSelectedOption(option);
  };

  const handleBackToSections = () => {
    setSelectedSection(null);
    setSelectedOption(null);
  };

  const renderConfigurationContent = () => {
    if (!selectedSection || !selectedOption) return null;

    const OptionComponent = selectedOption.component;

    if (OptionComponent) {
      // Props específicas para cada componente
      const componentProps = {
        clientId: clientId,
        onClose: () => setSelectedOption(null),
        embedded: true,
        // Para CategoriesCrud, pasar el defaultTab si está especificado
        ...(selectedOption.defaultTab && {
          defaultTab: selectedOption.defaultTab,
        }),
        // Para UsersSection, pasar el tab inicial si está especificado
        ...(selectedOption.defaultTab &&
          selectedOption.component === UsersSection && {
            initialTab: selectedOption.defaultTab,
          }),
        // Para SuppliersManagement, necesita props específicas
        ...(selectedOption.component === SuppliersManagement && {
          token: null, // Se obtendrá del contexto de autenticación
          mantisBusiness: { _id: clientId }, // Mock del objeto mantisBusiness
          user: null, // Se obtendrá del contexto de autenticación
        }),
      };

      return (
        <div className="h-full">
          <OptionComponent {...componentProps} />
        </div>
      );
    }

    return (
      <div className="p-8">
        <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200 mb-4">
          {selectedOption.name}
        </h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {selectedOption.description}
        </p>
        <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400 italic">
            Las opciones de configuración para esta sección estarán disponibles
            pronto.
          </p>
        </div>
      </div>
    );
  };

  const breadcrumbs = [
    { label: "Configuración", onClick: handleBackToSections },
    ...(selectedSection
      ? [
          {
            label: selectedSection.name,
            onClick: () => setSelectedOption(null),
          },
        ]
      : []),
    ...(selectedOption ? [{ label: selectedOption.name }] : []),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mostrar botón Volver solo cuando estamos en una subsección */}
              {(selectedSection || selectedOption) && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedOption) {
                        // Si estamos viendo una opción, volver a la sección
                        setSelectedOption(null);
                      } else if (selectedSection) {
                        // Si estamos viendo una sección, volver al inicio
                        setSelectedSection(null);
                      }
                    }}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Volver</span>
                  </Button>

                  <div className="h-6 w-px bg-slate-200 dark:bg-slate-700"></div>
                </>
              )}

              {/* Breadcrumbs */}
              <nav className="flex items-center space-x-2 text-sm">
                <Home className="h-4 w-4 text-slate-400" />
                {breadcrumbs.map((crumb, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                    {index === breadcrumbs.length - 1 ? (
                      <span className="text-slate-900 dark:text-slate-100 font-medium">
                        {crumb.label}
                      </span>
                    ) : (
                      <button
                        onClick={crumb.onClick}
                        className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                      >
                        {crumb.label}
                      </button>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded flex items-center justify-center shadow-lg">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                  Configuración del Sistema
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Administre la configuración y preferencias de su negocio
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="flex flex-col items-center">
              <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                Cargando configuración...
              </p>
            </div>
          </div>
        ) : selectedSection && selectedOption ? (
          // Configuration Content View
          <div className="px-8 py-6">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              {/* Option Header */}
              <div className="px-8 py-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-12 h-12 bg-gradient-to-r ${selectedSection.gradient} rounded flex items-center justify-center shadow-lg`}
                  >
                    <span className="text-lg text-white">
                      {selectedOption.icon}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                      {selectedOption.name}
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400">
                      {selectedOption.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Configuration Content */}
              <div className="min-h-[600px]">
                {renderConfigurationContent()}
              </div>
            </div>
          </div>
        ) : selectedSection ? (
          // Section Options View
          <div className="px-8 py-6">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-4">
                <div
                  className={`w-16 h-16 bg-gradient-to-r ${selectedSection.gradient} rounded-xl flex items-center justify-center shadow-lg`}
                >
                  <span className="text-2xl text-white">
                    {selectedSection.icon}
                  </span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
                    {selectedSection.name}
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-400">
                    {selectedSection.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {selectedSection.options.map((option) => (
                <motion.div
                  key={option.id}
                  whileHover={{ y: -4, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOptionSelect(option)}
                  className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-r ${selectedSection.gradient} rounded flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow`}
                    >
                      <span className="text-lg text-white">{option.icon}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {option.status === "active" && (
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700"
                        >
                          Activo
                        </Badge>
                      )}
                      {option.status === "coming_soon" && (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-700"
                        >
                          Próximamente
                        </Badge>
                      )}
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                    {option.name}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {option.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          // Main Sections Overview
          <div className="px-6 py-12">
            <div className="w-full">
              {/* Configuration Sections Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredSections.map((section, index) => (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -8, scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSectionSelect(section)}
                    className="group relative overflow-hidden bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    {/* Background Gradient */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${section.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                    ></div>

                    {/* Content */}
                    <div className="relative">
                      <div className="flex items-start justify-between mb-6">
                        <div
                          className={`w-16 h-16 bg-gradient-to-r ${section.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}
                        >
                          <span className="text-2xl text-white">
                            {section.icon}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant="outline"
                            className="bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600"
                          >
                            {section.options.length} opción
                            {section.options.length !== 1 ? "es" : ""}
                          </Badge>
                          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors duration-200" />
                        </div>
                      </div>

                      <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-3 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors duration-200">
                        {section.name}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                        {section.description}
                      </p>

                      {/* Options Preview */}
                      <div className="space-y-2">
                        {section.options.slice(0, 2).map((option) => (
                          <div
                            key={option.id}
                            className="flex items-center space-x-3 text-sm"
                          >
                            <span className="text-lg">{option.icon}</span>
                            <span className="text-slate-700 dark:text-slate-300">
                              {option.name}
                            </span>
                            {option.status === "coming_soon" && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-yellow-50 text-yellow-600 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-700"
                              >
                                Próximamente
                              </Badge>
                            )}
                          </div>
                        ))}
                        {section.options.length > 2 && (
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            +{section.options.length - 2} más...
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 